import { useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Api } from "../lib/api.js";
import { DismissButton } from "./DismissButton.js";

/**
 * "Highly Recommended" row — items other profiles in the household have sent
 * to this profile. Each card overlays a small badge crediting the sender
 * (their forecourt mark + name), shows their note as a quoted whisper at the
 * top of the card, and exposes a dismiss × in the corner so the recipient
 * can clear something they don't want to watch.
 *
 * Item shape comes from /tv/recommendations → highly_recommended section.
 */
export interface RecommendedItem {
  id: string;
  rec_id: string;
  kind: string;
  title: string;
  thumbnail_url: string | null;
  tmdb_backdrop_url?: string | null;
  duration_s?: number | null;
  category?: string | null;
  creator_name?: string | null;
  tmdb_rating?: number | null;
  tmdb_genres?: string[] | null;
  rec_note: string | null;
  recommended_by: string;
  recommended_by_mark: string | null;
  recommended_at: string;
}

export function HighlyRecommendedStrip({
  label,
  items,
  profileId,
  onRefresh,
}: {
  label: string;
  items: RecommendedItem[];
  profileId: string;
  onRefresh?: () => void;
}): JSX.Element {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const qc = useQueryClient();
  const dismiss = useMutation({
    mutationFn: (recId: string) => Api.recsTo.dismiss(recId),
    onMutate: async (recId: string) => {
      const item = items.find((it) => it.rec_id === recId);
      if (!item) return;
      const key = ["dismissals", profileId];
      await qc.cancelQueries({ queryKey: key });
      qc.setQueryData<{ itemIds: string[] }>(key, (old) => ({
        itemIds: [...new Set([...(old?.itemIds ?? []), item.id])],
      }));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["recs", profileId] });
      void qc.invalidateQueries({ queryKey: ["dismissals", profileId] });
    },
  });

  function scrollBy(dir: -1 | 1): void {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  }

  // Group consecutive items by sender so a single "From Doug:" header can
  // span multiple picks when they came from the same person.
  return (
    <section className="mb-10">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-baseline gap-3">
          <div className="section-label">{label}</div>
          <div className="text-parchment/60 text-xs">
            {items.length} pick{items.length === 1 ? "" : "s"} from your household
          </div>
        </div>
        <div className="flex gap-1">
          <button className="pill-ghost p-2" onClick={() => scrollBy(-1)} aria-label="scroll left">
            <ChevronLeft size={16} />
          </button>
          <button className="pill-ghost p-2" onClick={() => scrollBy(1)} aria-label="scroll right">
            <ChevronRight size={16} />
          </button>
          {onRefresh && (
            <button className="pill-ghost p-2 text-parchment/50 hover:text-gold" onClick={onRefresh} aria-label="refresh">
              <RefreshCw size={14} />
            </button>
          )}
        </div>
      </div>
      <div
        ref={scrollerRef}
        className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth"
        style={{ scrollbarWidth: "thin" }}
      >
        {items.map((it) => (
          <RecommendedCard
            key={it.rec_id}
            item={it}
            onDismiss={() => dismiss.mutate(it.rec_id)}
            dismissing={dismiss.isPending}
          />
        ))}
      </div>
    </section>
  );
}

function RecommendedCard({
  item,
  onDismiss,
  dismissing,
}: {
  item: RecommendedItem;
  onDismiss: () => void;
  dismissing: boolean;
}): JSX.Element {
  const rating = typeof item.tmdb_rating === "number"
    ? Math.round(item.tmdb_rating * 10) / 10
    : null;

  return (
    <div className="min-w-[320px] snap-start relative group">
      <Link
        to={`/watch/${encodeURIComponent(item.id)}`}
        className="card group block overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-goldHi hover:scale-[1.04] transition-transform duration-200 ease-spring"
      >
        <div className="aspect-video bg-elev relative">
          {item.thumbnail_url ? (
            <img
              src={item.thumbnail_url}
              alt=""
              loading="lazy"
              referrerPolicy="no-referrer"
              className="w-full h-full object-cover"
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center font-display text-parchment/40 text-2xl">
              {item.title.slice(0, 2).toUpperCase()}
            </div>
          )}
          {/* Vermilion glow strip across the top to mark this as a personal pick */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-vermilion via-gold to-vermilion" />
          {rating !== null && (
            <span
              className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-[11px] bg-ink/85 border border-gold/40 font-display tabular-nums text-goldHi"
              title={`TMDB rating ${rating}/10`}
            >
              ★ {rating}
            </span>
          )}
        </div>

        <div className="p-3">
          {/* Sender attribution chip — gold mark + display name */}
          <div className="flex items-center gap-2 mb-2">
            <span className="forecourt-mark w-7 h-7 text-sm flex-shrink-0">
              {item.recommended_by_mark ?? "★"}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-display tracking-smallcaps text-gold uppercase">From</div>
              <div className="text-ivory text-xs leading-tight font-display tracking-smallcaps truncate">
                {item.recommended_by}
              </div>
            </div>
            <span className="text-parchment/50 text-[10px] tabular-nums whitespace-nowrap">
              {relTime(item.recommended_at)}
            </span>
          </div>

          {/* Optional note — quoted, italic, vermilion accent */}
          {item.rec_note && (
            <blockquote className="relative pl-3 mb-2 border-l-2 border-vermilion/70 text-parchment text-xs italic leading-snug line-clamp-2">
              <span className="text-vermilion/80 select-none mr-0.5">"</span>
              {item.rec_note}
              <span className="text-vermilion/80 select-none ml-0.5">"</span>
            </blockquote>
          )}

          <div className="text-ivory text-sm leading-tight line-clamp-2 min-h-[2.5em]">
            {item.title}
          </div>
          <div className="text-parchment text-xs mt-1 truncate">
            {item.creator_name ?? item.category ?? item.kind.replace("_", " ")}
          </div>
        </div>
      </Link>

      <DismissButton itemTitle={item.title} onDismiss={onDismiss} disabled={dismissing} />
    </div>
  );
}

function relTime(iso: string): string {
  const d = new Date(iso).getTime();
  if (!Number.isFinite(d)) return "";
  const diffSec = Math.floor((Date.now() - d) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 604800) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}
